import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import io from 'socket.io-client';
import CryptoJS from 'crypto-js';

const SERVER_URL = 'https://silent-uqpx.onrender.com';

function ChatRoom() {
  const { roomId } = useParams();
  const location = useLocation();
  const { t } = useTranslation('chat');

  const [password, setPassword] = useState(location.state?.password || '');
  const [socket, setSocket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMsg, setInputMsg] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!password) {
      setError(t('errors.noPassword'));
      return;
    }

    const s = io(SERVER_URL, { transports: ['websocket'] });
    setSocket(s);

    s.on('connect', () => {
      s.emit('join-room', { roomId, password });
    });

    s.on('room-join-error', ({ error }) => setError(error));
    s.on('room-joined', () => console.log(t('logs.roomJoined')));

    s.on('chat-message', ({ message: ciphertext, userId }) => {
      try {
        const bytes = CryptoJS.AES.decrypt(ciphertext, password);
        const plaintext = bytes.toString(CryptoJS.enc.Utf8) || t('errors.decryptionError');

        setMessages(prev => [
          ...prev,
          userId === s.id 
            ? `${t('messagePrefix.you')}: ${plaintext}`
            : `${userId}: ${plaintext}`
        ]);
      } catch (err) {
        console.error(t('logs.decryptionError'), err);
        setMessages(prev => [...prev, t('errors.corruptedMessage')]);
      }
    });

    s.on('user-joined', ({ userId }) => {
      setMessages(prev => [...prev, t('system.userJoined', { userId })]);
    });

    s.on('user-left', ({ userId }) => {
      setMessages(prev => [...prev, t('system.userLeft', { userId })]);
    });

    return () => s.disconnect();
  }, [roomId, password, t]);

  const sendMessage = () => {
    if (!socket || !inputMsg.trim()) return;
    
    const ciphertext = CryptoJS.AES.encrypt(inputMsg.trim(), password).toString();
    socket.emit('chat-message', { message: ciphertext });
    setInputMsg('');
  };

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <h2 style={styles.errorHeading}>{error}</h2>
        <p style={styles.errorText}>{t('errors.joinFailed')}</p>
      </div>
    );
  }

  return (
    <div style={styles.mainContainer}>
      <div style={styles.chatWrapper}>
        <h2 style={styles.roomTitle}>🔒 {t('roomTitle')} • {roomId}</h2>
        <div style={styles.separator} />

        <div style={styles.messagesContainer}>
          {messages.map((m, i) => {
            const isMe = m.startsWith(`${t('messagePrefix.you')}:`);
            const isSystem = m.includes(t('system.userJoined')) || m.includes(t('system.userLeft'));
            
            return (
              <div
                key={i}
                style={{
                  ...styles.messageBubble,
                  ...(isSystem ? styles.systemMessage : {}),
                  ...(isMe ? styles.myMessage : styles.otherMessage)
                }}
              >
                {isSystem ? `👤 ${m.replace(/ \w+$/, '')}` : m.replace(`${t('messagePrefix.you')}: `, '')}
              </div>
            );
          })}
        </div>

        <div style={styles.inputContainer}>
          <input
            style={styles.input}
            placeholder={t('inputPlaceholder')}
            value={inputMsg}
            onChange={(e) => setInputMsg(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
          />
          <button style={styles.button} onClick={sendMessage}>
            {t('sendButton')}
          </button>
        </div>

        <div style={styles.encryptionNote}>
          <span style={styles.lockIcon}>🔒</span>
          {t('encryptionNote')}
        </div>
      </div>
    </div>
  );
}
const styles = {
  mainContainer: {
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2%',
    boxSizing: 'border-box',
  },
  chatWrapper: {
    width: '100%',
    maxWidth: '800px',
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    padding: 'clamp(16px, 3%, 24px)',
    position: 'relative',
    margin: '0 2%',
    height: 'auto',
    minHeight: '50vh',
    maxHeight: '90vh',
  },
  roomTitle: {
    fontSize: 'clamp(1.2rem, 3vw, 1.5rem)',
    fontWeight: '600',
    color: '#000',
    marginBottom: 'clamp(8px, 1.5vw, 12px)',
    textAlign: 'center',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    padding: '0 1rem',
  },
  separator: {
    height: '1px',
    backgroundColor: '#eee',
    margin: '0 0 clamp(16px, 2vw, 24px) 0',
  },
  messagesContainer: {
    height: '60vh',
    maxHeight: '50vh',
    overflowY: 'auto',
    marginBottom: 'clamp(12px, 2vw, 20px)',
    padding: '1rem',
    backgroundColor: '#fff',
    borderRadius: '8px',
  },
  messageBubble: {
    marginBottom: 'clamp(8px, 1vw, 12px)',
    padding: 'clamp(8px, 2vw, 12px) clamp(12px, 3vw, 16px)',
    borderRadius: '20px',
    maxWidth: '85%',
    wordBreak: 'break-word',
    fontSize: 'clamp(0.8rem, 2.5vw, 0.95rem)',
    lineHeight: 1.4,
    color: '#000',
  },
  myMessage: {
    backgroundColor: '#fff',
    border: '1px solid #000',
    marginLeft: 'auto',
    maxWidth: '90%',
  },
  otherMessage: {
    backgroundColor: '#fff',
    border: '1px solid #000',
    marginRight: 'auto',
    maxWidth: '90%',
  },
  systemMessage: {
    fontSize: 'clamp(0.75rem, 2.5vw, 0.85rem)',
    margin: 'clamp(4px, 1vw, 8px) auto',
  },
  inputContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: 'clamp(8px, 2vw, 12px)',
    marginBottom: 'clamp(12px, 2vw, 16px)',
    '@media (max-width: 480px)': {
      flexDirection: 'column'
    }
  },
  input: {
    flex: 1,
    padding: 'clamp(10px, 2vw, 12px) clamp(12px, 3vw, 16px)',
    border: '1px solid #000',
    borderRadius: '25px',
    fontSize: 'clamp(14px, 3vw, 16px)',
    outline: 'none',
    minWidth: '120px',
    '&:focus': {
      borderColor: '#000',
    },
  },
  button: {
    padding: 'clamp(10px, 2vw, 12px) clamp(16px, 4vw, 24px)',
    backgroundColor: '#000',
    color: '#fff',
    border: 'none',
    borderRadius: '25px',
    cursor: 'pointer',
    fontSize: 'clamp(14px, 3vw, 16px)',
    fontWeight: '600',
    transition: 'background-color 0.2s',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: '#333',
    },
  },
  encryptionNote: {
    position: 'relative',
    bottom: 'auto',
    left: 'auto',
    transform: 'none',
    textAlign: 'center',
    marginTop: 'clamp(16px, 2vw, 24px)',
    padding: '0 1rem',
    fontSize: 'clamp(0.75rem, 2.5vw, 0.85rem)',
    '@media (min-width: 768px)': {
      position: 'absolute',
      bottom: '-30px',
      left: '50%',
      transform: 'translateX(-50%)',
      marginTop: 0
    }
  },
  errorContainer: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '20px',
  },
  errorHeading: {
    color: '#000',
    fontSize: '1.5rem',
    marginBottom: '1rem',
  },
  errorText: {
    color: '#666',
    fontSize: '1rem',
  },
};

export default ChatRoom;
